/* Content cards prev/next + progress indicator (OL-3527, epic OL-3521
   Enhanced PDP v2).
   No library: the track is a native horizontal scroll-snap row when a
   carousel is enabled, so prev/next just scroll it by one cell width, and
   the indicator is read straight from the track's own scroll geometry -
   the browser's scroll position is the only state, nothing to keep in sync.

   The indicator is one chip per card, with EVERY on-screen card's chip
   active at once - not a single "leading card" chip. Lighting only the
   leading one implies one-card-at-a-time paging, which misreads as soon as
   several cards are visible together: 3 of 4 cards on screen, one "next"
   click reaching the end of the scrollable range, yet a single-active model
   would light the 2nd of 4 chips and imply two more clicks remain. Marking
   the whole visible run instead makes the active chips the cards you can
   see and the grey ones what is left to reach, on whichever side it sits.

   Visibility is measured by overlapping each cell's box against the track's
   own box, so the JS needs to know nothing about card widths, gaps or
   max_cards_per_row - it reads the same geometry the browser already
   resolved. This replaced a proportional thumb bar which was correct in
   principle but failed invisibly: a thumb that computes to zero width is
   just gone, with nothing on screen to say why. Chips always render, so a
   miscalculation shows up as the wrong chips lit rather than a blank rail. */
(() => {
  function wire(nav) {
    if (nav.dataset.ccWired) return;
    nav.dataset.ccWired = 'true';

    const wrapper = nav.closest('.content-cards');
    const track = wrapper && wrapper.querySelector('[data-content-cards-track]');
    const prev = nav.querySelector('[data-content-cards-prev]');
    const next = nav.querySelector('[data-content-cards-next]');
    const dashes = [...nav.querySelectorAll('[data-content-cards-dashes] .content-cards__dash')];
    if (!track || !prev || !next) return;

    const step = () => {
      const cell = track.querySelector('.content-cards__cell');
      return cell ? cell.getBoundingClientRect().width + 20 : track.clientWidth;
    };

    prev.addEventListener('click', () => {
      track.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    next.addEventListener('click', () => {
      track.scrollBy({ left: step(), behavior: 'smooth' });
    });

    /* Sub-pixel tolerance: scrollLeft rarely lands on exactly 0 or exactly
       the maximum after a smooth scroll or a trackpad flick, and an arrow
       that stays live at 0.4px from the end reads as a glitch - one more
       click that does nothing. */
    const EDGE_TOLERANCE = 1;

    /* A cell counts as on screen once at least half of it is inside the
       scrollport, so the trailing sliver of a peeking card does not claim a
       chip of its own - "1.3 cards visible" reads as one. */
    const VISIBLE_RATIO = 0.5;

    let ticking = false;
    const update = () => {
      ticking = false;

      /* A track that has not been laid out yet measures 0 wide, which looks
         identical to "nothing to scroll" and would write that wrong state
         out permanently. Skip instead; the ResizeObserver below fires again
         the moment the track has a real box. */
      if (!track.clientWidth) return;

      const scrollable = track.scrollWidth - track.clientWidth;
      const atStart = scrollable <= 0 || track.scrollLeft <= EDGE_TOLERANCE;
      const atEnd = scrollable <= 0 || track.scrollLeft >= scrollable - EDGE_TOLERANCE;

      /* Nothing further to reveal that way, so the control that would do it
         is dead - say so rather than leaving it live and inert. */
      prev.disabled = atStart;
      next.disabled = atEnd;

      if (!dashes.length) return;

      /* Every chip whose card is currently on screen is active, not just one
         "leading" card: with several cards visible at once the active run is
         as wide as what is on screen, and the grey remainder shows what is
         left to reach on either side. Measured by overlap against the
         track's own box rather than by index arithmetic, so it needs to know
         nothing about card widths, gaps or max_cards_per_row. */
      const view = track.getBoundingClientRect();
      track.querySelectorAll('.content-cards__cell').forEach((cell, i) => {
        if (!dashes[i]) return;
        const box = cell.getBoundingClientRect();
        const shown = Math.min(box.right, view.right) - Math.max(box.left, view.left);
        dashes[i].classList.toggle('is-active', shown >= box.width * VISIBLE_RATIO);
      });
    };

    const schedule = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    track.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    /* Re-measure whenever the track's own box changes, not just on window
       resize. A single measurement taken at wiring time is unreliable: the
       track is a plain grid until the carousel's breakpoint applies, and a
       grid stacks its cards vertically, so scrollWidth equals clientWidth
       and the track reads as "nothing to scroll" - which pinned the
       indicator to a full-width thumb that then never corrected itself,
       because scroll and resize events alone never fire in that state.
       Observing the track catches the grid-to-flex switch, the theme
       editor's own re-renders and preview-size changes, and late layout
       shifts as card images load. */
    if ('ResizeObserver' in window) {
      new ResizeObserver(schedule).observe(track);
    }

    update();
  }

  function wireAll() {
    document.querySelectorAll('[data-content-cards-nav]').forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireAll);
  } else {
    wireAll();
  }
  document.addEventListener('shopify:section:load', wireAll);
})();
